import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OctagonX, ShieldAlert, Zap } from 'lucide-react';
import { useKillSwitchStatus, useExchangeCBList } from '@/api/hooks/use-system';
import { cn } from '@/shared/lib/utils';

interface AlertItem {
  id: string;
  icon: React.ReactNode;
  message: string;
  linkTo: string;
  variant: 'destructive' | 'warning';
  pulse?: boolean;
}

/**
 * LOK-1034: Persistent global alert bar shown on ALL pages when critical safety conditions are active:
 * - Global kill switch active (red, pulsing)
 * - Exchange circuit breaker tripped:
 *   - OPEN status → red (destructive), pulsing — trading halted for that exchange
 *   - HALF_OPEN status → amber (warning) — recovery in progress
 *
 * Polls independently and renders above the main content area in a sticky position.
 */
export function GlobalAlertBar() {
  const { t } = useTranslation('risk');
  const { data: killSwitch } = useKillSwitchStatus();
  const { data: cbList } = useExchangeCBList();

  const alerts: AlertItem[] = [];

  // Kill switch active — always destructive + pulsing
  if (killSwitch?.active) {
    alerts.push({
      id: 'kill-switch',
      icon: <OctagonX className="h-4 w-4 shrink-0" />,
      message: killSwitch.reason
        ? `KILL SWITCH ACTIVE — All trading halted. ${killSwitch.reason}`
        : 'KILL SWITCH ACTIVE — All trading halted',
      linkTo: '/risk',
      variant: 'destructive',
      pulse: true,
    });
  }

  // Circuit breakers — separate OPEN (critical) from HALF_OPEN (warning)
  const trippedCBs = cbList?.data?.filter((cb) => cb.status !== 'CLOSED' && cb.status !== 'closed');
  if (trippedCBs && trippedCBs.length > 0) {
    const openCBs = trippedCBs.filter((cb) => cb.status === 'OPEN');
    const halfOpenCBs = trippedCBs.filter((cb) => cb.status === 'HALF_OPEN');

    if (openCBs.length > 0) {
      const exchanges = openCBs.map((cb) => cb.exchange).join(', ');
      alerts.push({
        id: 'cb-open',
        icon: <Zap className="h-4 w-4 shrink-0" />,
        message: `${t('circuitBreaker.title')}: ${exchanges} — Trading halted. Manual recovery required.`,
        linkTo: '/risk',
        variant: 'destructive',
        pulse: true,
      });
    }

    if (halfOpenCBs.length > 0) {
      const exchanges = halfOpenCBs.map((cb) => cb.exchange).join(', ');
      alerts.push({
        id: 'cb-half-open',
        icon: <Zap className="h-4 w-4 shrink-0" />,
        message: `${t('circuitBreaker.title')}: ${exchanges} — Recovery in progress. Trading restricted.`,
        linkTo: '/risk',
        variant: 'warning',
      });
    }
  }

  if (alerts.length === 0) return null;

  return (
    <div className="sticky top-0 z-40 flex flex-col" role="status" aria-live="assertive">
      {alerts.map((alert) => (
        <Link
          key={alert.id}
          to={alert.linkTo}
          className={cn(
            'flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors hover:opacity-90',
            alert.variant === 'destructive' ? 'bg-red-600 text-white' : 'bg-amber-600 text-white',
            alert.pulse && 'animate-pulse',
          )}
        >
          {alert.icon}
          <span className="flex-1">{alert.message}</span>
          <span className="text-xs opacity-70">
            {t('circuitBreaker.viewDetails', 'View details →')}
          </span>
          <ShieldAlert className="h-3.5 w-3.5 opacity-60" />
        </Link>
      ))}
    </div>
  );
}
